import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Ic } from '../icons';
import { tl } from '../data';
import './TournamentAwards.css';

export const tournamentPodium = (tour) => tour?.status === 'done' && Array.isArray(tour.podium)
  ? tour.podium.filter((entry) => [1, 2, 3].includes(entry.place) && typeof entry.team === 'string' && entry.team.trim())
    .sort((a, b) => a.place - b.place)
  : [];

const METALS = ['gold', 'silver', 'bronze'];
const ROMAN = ['I', 'II', 'III'];
const money = (value) => new Intl.NumberFormat('ru-RU').format(value);

function AwardsPanel({ tour, lang, onExpand, onClose }) {
  const podium = tournamentPodium(tour);
  const [index, setIndex] = useState(0);
  const id = useId();
  const tabs = useRef([]);
  const kz = lang === 'kz';
  const selected = Math.min(index, podium.length - 1);
  const winner = podium[selected];
  if (!winner) return null;
  const move = (delta) => setIndex((selected + delta + podium.length) % podium.length);
  const label = (place) => kz ? `${place}-орын` : `${place}-е место`;
  const keyboard = (event) => {
    let next;
    if (event.key === 'ArrowRight') next = (selected + 1) % podium.length;
    if (event.key === 'ArrowLeft') next = (selected - 1 + podium.length) % podium.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = podium.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setIndex(next);
    tabs.current[next]?.focus();
  };

  return <section className={`tournament-awards is-${METALS[winner.place - 1]}`} aria-label={kz ? 'Турнир жеңімпаздары' : 'Победители турнира'}>
    <header className="tournament-awards__header">
      <div><span className="tournament-awards__eyebrow">{kz ? 'Турнир аяқталды' : 'Турнир завершён'}<i />{tour.disc}</span>
        <h2>{tl(tour, 'title', lang)}</h2>
        <p>{tl(tour, 'date', lang)}</p>
      </div>
      {onClose ? <button type="button" className="tournament-awards__icon" onClick={onClose} aria-label={kz ? 'Жабу' : 'Закрыть победителей'} autoFocus><Ic name="x" size={22} /></button>
        : onExpand && <button type="button" className="tournament-awards__icon" onClick={onExpand} aria-label={kz ? 'Жеңімпаздарды бөлек терезеде ашу' : 'Открыть победителей в отдельном окне'}><Ic name="external" size={20} /></button>}
    </header>

    <div id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-tab-${winner.place}`} tabIndex={0} className="tournament-awards__body">
      <div className="tournament-awards__medallion" aria-hidden="true"><span className="tournament-awards__roman">{ROMAN[winner.place - 1]}</span><Ic name="trophy" size={34} className="tournament-awards__trophy" /></div>
      <div className="tournament-awards__winner" key={winner.place}>
        <span className="tournament-awards__rank">{label(winner.place)}{winner.place === 1 && <><i />{kz ? 'Чемпиондар' : 'Чемпионы'}</>}</span>
        <h3>{winner.team}</h3>
        {Number.isFinite(winner.prize) && <div className="tournament-awards__prize"><span>{kz ? 'Команда жүлдесі' : 'Приз команды'}</span><strong>{money(winner.prize)} <small>₸</small></strong></div>}
      </div>
      <div className="tournament-awards__arrows">
        <button type="button" className="tournament-awards__icon" onClick={() => move(-1)} aria-label={kz ? 'Алдыңғы жүлдегер' : 'Предыдущий призёр'} disabled={podium.length < 2}><Ic name="chevR" size={20} style={{ transform: 'rotate(180deg)' }} /></button>
        <span aria-hidden="true">{selected + 1} / {podium.length}</span>
        <button type="button" className="tournament-awards__icon" onClick={() => move(1)} aria-label={kz ? 'Келесі жүлдегер' : 'Следующий призёр'} disabled={podium.length < 2}><Ic name="chevR" size={20} /></button>
      </div>
    </div>

    <div className="tournament-awards__tabs" role="tablist" aria-label={kz ? 'Жүлделі орындар' : 'Призовые места'} onKeyDown={keyboard}>
      {podium.map((entry, i) => <button key={entry.place} ref={(node) => { tabs.current[i] = node; }} type="button" role="tab"
        id={`${id}-tab-${entry.place}`} aria-controls={`${id}-panel`} aria-selected={i === selected} tabIndex={i === selected ? 0 : -1}
        onClick={() => setIndex(i)} className={`tournament-awards__tab is-${METALS[entry.place - 1]}`}>
        <span className="tournament-awards__tab-place">{String(entry.place).padStart(2, '0')}<span>{label(entry.place)}</span></span>
        <strong>{entry.team}</strong>
      </button>)}
    </div>
    <footer className="tournament-awards__footer"><span>OO EkoTime проект Федерация Киберспорта СКО</span><span>{kz ? 'Жүлде қоры' : 'Призовой фонд'} <b>{tl(tour, 'prize', lang)}</b></span></footer>
  </section>;
}

function AwardsDialog({ tour, lang, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const dialog = ref.current;
    const trigger = document.activeElement;
    const body = document.body;
    const top = window.scrollY;
    const previous = { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow };
    Object.assign(body.style, { position: 'fixed', top: `-${top}px`, width: '100%', overflow: 'hidden' });
    dialog.showModal();
    return () => {
      dialog.close();
      Object.assign(body.style, previous);
      window.scrollTo({ top, behavior: 'instant' });
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, []);
  return createPortal(<dialog ref={ref} className="tournament-awards-dialog" aria-label={lang === 'kz' ? 'Турнир жеңімпаздары' : 'Победители турнира'}
    onCancel={(event) => { event.preventDefault(); onClose(); }} onClick={(event) => {
      if (event.target !== event.currentTarget) return;
      const box = event.currentTarget.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) onClose();
    }}>
    <AwardsPanel tour={tour} lang={lang} onClose={onClose} />
  </dialog>, document.body);
}

export function TournamentWinnersButton({ tour, lang = 'ru', className = '' }) {
  const [open, setOpen] = useState(false);
  if (!tournamentPodium(tour).length) return null;
  return <><button type="button" className={`tournament-awards-entry ${className}`} onClick={(event) => { event.stopPropagation(); setOpen(true); }}><Ic name="trophy" size={19} />{lang === 'kz' ? 'Жеңімпаздар' : 'Победители'}<Ic name="chevR" size={18} /></button>
    {open && <AwardsDialog tour={tour} lang={lang} onClose={() => setOpen(false)} />}</>;
}

export default function TournamentAwards({ tour, lang = 'ru' }) {
  const [open, setOpen] = useState(false);
  if (!tournamentPodium(tour).length) return null;
  return <><AwardsPanel key={tour.id} tour={tour} lang={lang} onExpand={() => setOpen(true)} />
    {open && <AwardsDialog tour={tour} lang={lang} onClose={() => setOpen(false)} />}</>;
}
